/**
 * Deck Studio — universal deck creator.
 *
 * Every authenticated user can build a modern, org-branded PowerPoint here.
 * The user supplies content into a fixed vocabulary of slide archetypes; the
 * SERVER computes all layout (POST /preview returns drawing primitives that
 * SvgSlide draws verbatim), and export produces a native, editable .pptx.
 * Department-bound users can insert live numbers from the modules they are
 * already allowed to see; the scoping happens server-side.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SvgSlide from './SvgSlide';

const API = process.env.REACT_APP_API_URL || '';

const jfetch = (path, opts = {}) =>
  fetch(`${API}/api/deck-studio${path}`, {
    credentials: 'include',
    headers: opts.body && !(opts.body instanceof FormData) ? { 'Content-Type': 'application/json' } : undefined,
    ...opts,
  }).then(async (r) => {
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Request failed (${r.status})`);
    return r.json();
  });

// ── Small UI atoms ──────────────────────────────────────────────────────────

const Label = ({ children }) => (
  <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{children}</label>
);

const Input = (props) => (
  <input {...props} className={`w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700/30 focus:border-emerald-700 ${props.className || ''}`} />
);

const TextArea = (props) => (
  <textarea {...props} className={`w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-700/30 focus:border-emerald-700 ${props.className || ''}`} />
);

const Btn = ({ tone = 'ghost', className = '', ...props }) => {
  const tones = {
    primary: 'bg-emerald-900 text-white hover:bg-emerald-800',
    ghost: 'border border-slate-300 text-slate-700 hover:bg-slate-100',
    danger: 'text-red-700 hover:bg-red-50 border border-transparent',
  };
  return <button {...props} className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${tones[tone]} ${className}`} />;
};

// ── Field editors (driven by the server's archetype field metadata) ─────────

function BulletsEditor({ value, onChange, max }) {
  return (
    <TextArea rows={5} placeholder={`One item per line (up to ${max})`}
      value={(value || []).join('\n')}
      onChange={(e) => onChange(e.target.value.split('\n'))} />
  );
}

function KpisEditor({ value, onChange, feeds, onInsert, max = 4 }) {
  const kpis = value || [];
  const set = (i, k, v) => { const n = kpis.map((x) => ({ ...x })); n[i] = { ...n[i], [k]: v }; onChange(n); };
  return (
    <div className="space-y-2">
      {kpis.map((k, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <div className="col-span-3"><Input placeholder="Label" value={k.label || ''} onChange={(e) => set(i, 'label', e.target.value)} /></div>
          <div className="col-span-2"><Input placeholder="Value" value={k.value || ''} onChange={(e) => set(i, 'value', e.target.value)} /></div>
          <div className="col-span-3"><Input placeholder="Delta (e.g. +12% vs Q1)" value={k.delta || ''} onChange={(e) => set(i, 'delta', e.target.value)} /></div>
          <div className="col-span-3"><Input placeholder="Sparkline: 4, 6, 5, 9" value={(k.spark || []).join(', ')} onChange={(e) => set(i, 'spark', e.target.value.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n)))} /></div>
          <button className="col-span-1 text-slate-400 hover:text-red-600 text-lg leading-none" title="Remove" onClick={() => onChange(kpis.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <div className="flex gap-2">
        {kpis.length < max && <Btn onClick={() => onChange([...kpis, {}])}>+ KPI card</Btn>}
        <InsertFromData feeds={feeds} kind="kpis" onInsert={onInsert} />
      </div>
    </div>
  );
}

function ChartEditor({ value, onChange, feeds, onInsert }) {
  const c = value || { labels: [], series: [] };
  const set = (k, v) => onChange({ ...c, [k]: v });
  const setSeries = (i, k, v) => {
    const s = (c.series || []).map((x) => ({ ...x }));
    s[i] = { ...s[i], [k]: v };
    onChange({ ...c, series: s });
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-8">
          <Label>Category labels (comma separated)</Label>
          <Input placeholder="Feb, Mar, Apr, May" value={(c.labels || []).join(', ')}
            onChange={(e) => set('labels', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
        </div>
        <div className="col-span-2">
          <Label>Type</Label>
          <select className="w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" value={c.chartType || 'auto'} onChange={(e) => set('chartType', e.target.value)}>
            {['auto', 'column', 'bar', 'line', 'area', 'doughnut'].map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <Label>Stack</Label>
          <select className="w-full rounded-lg border border-slate-300 px-2 py-2 text-sm" value={c.stack || 'none'} onChange={(e) => set('stack', e.target.value)}>
            {['none', 'stacked', 'percent'].map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
      </div>
      {(c.series || []).map((s, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <div className="col-span-3"><Input placeholder="Series name" value={s.name || ''} onChange={(e) => setSeries(i, 'name', e.target.value)} /></div>
          <div className="col-span-8"><Input placeholder="Values: 12, 10, 11, 8" value={(s.values || []).join(', ')} onChange={(e) => setSeries(i, 'values', e.target.value.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n)))} /></div>
          <button className="col-span-1 text-slate-400 hover:text-red-600 text-lg leading-none" onClick={() => set('series', c.series.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      <div className="flex gap-2 items-center flex-wrap">
        {(c.series || []).length < 6 && <Btn onClick={() => set('series', [...(c.series || []), {}])}>+ Series</Btn>}
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={!!c.time} onChange={(e) => set('time', e.target.checked)} /> time series
        </label>
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-slate-600">Benchmark</span>
          <Input className="!w-20" placeholder="95" value={c.target ?? ''} onChange={(e) => set('target', e.target.value === '' ? null : Number(e.target.value))} />
          <select className="rounded-lg border border-slate-300 px-1.5 py-1.5 text-sm" value={c.targetDir || 'min'} onChange={(e) => set('targetDir', e.target.value)}>
            <option value="min">≥ compliant</option>
            <option value="max">≤ compliant</option>
          </select>
        </div>
        <InsertFromData feeds={feeds} kind="chart" onInsert={onInsert} />
      </div>
    </div>
  );
}

function TableEditor({ value, onChange, feeds, onInsert }) {
  const t = value || { columns: [], rows: [] };
  const asText = [t.columns, ...(t.rows || [])].filter((r) => r && r.length).map((r) => r.join('\t')).join('\n');
  return (
    <div className="space-y-2">
      <TextArea rows={7} placeholder={'Paste cells from Excel, or type rows with TAB between columns.\nFirst row becomes the header.'}
        defaultValue={asText}
        onBlur={(e) => {
          const lines = e.target.value.split('\n').map((l) => l.split('\t').map((c) => c.trim())).filter((r) => r.some(Boolean));
          onChange({ columns: lines[0] || [], rows: lines.slice(1) });
        }} />
      <InsertFromData feeds={feeds} kind="table" onInsert={onInsert} />
    </div>
  );
}

function ColumnsEditor({ value, onChange, max }) {
  const cols = value || [];
  const set = (i, k, v) => { const n = cols.map((x) => ({ ...x })); n[i] = { ...n[i], [k]: v }; onChange(n); };
  return (
    <div className="grid grid-cols-3 gap-3">
      {cols.map((c, i) => (
        <div key={i} className="space-y-1.5 rounded-lg border border-slate-200 p-2">
          <div className="flex items-center gap-1">
            <Input placeholder="Header" value={c.header || ''} onChange={(e) => set(i, 'header', e.target.value)} />
            <button className="text-slate-400 hover:text-red-600 text-lg leading-none px-1" onClick={() => onChange(cols.filter((_, j) => j !== i))}>×</button>
          </div>
          <TextArea rows={5} placeholder="One point per line" value={(c.items || []).join('\n')} onChange={(e) => set(i, 'items', e.target.value.split('\n'))} />
        </div>
      ))}
      {cols.length < max && (
        <button className="rounded-lg border-2 border-dashed border-slate-300 text-slate-500 text-sm hover:border-emerald-700 hover:text-emerald-800 min-h-[80px]" onClick={() => onChange([...cols, {}])}>+ Column</button>
      )}
    </div>
  );
}

function MilestonesEditor({ value, onChange, max }) {
  const ms = value || [];
  const set = (i, k, v) => { const n = ms.map((x) => ({ ...x })); n[i] = { ...n[i], [k]: v }; onChange(n); };
  return (
    <div className="space-y-2">
      {ms.map((m, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <div className="col-span-3"><Input placeholder="Date (Jul 2026)" value={m.date || ''} onChange={(e) => set(i, 'date', e.target.value)} /></div>
          <div className="col-span-7"><Input placeholder="Milestone" value={m.label || ''} onChange={(e) => set(i, 'label', e.target.value)} /></div>
          <label className="col-span-1 flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={!!m.done} onChange={(e) => set(i, 'done', e.target.checked)} />done</label>
          <button className="col-span-1 text-slate-400 hover:text-red-600 text-lg leading-none" onClick={() => onChange(ms.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      {ms.length < max && <Btn onClick={() => onChange([...ms, {}])}>+ Milestone</Btn>}
    </div>
  );
}

function MultiplesEditor({ value, onChange, max }) {
  const charts = value || [];
  const set = (i, k, v) => { const n = charts.map((x) => ({ ...x })); n[i] = { ...n[i], [k]: v }; onChange(n); };
  return (
    <div className="space-y-2">
      {charts.map((m, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <div className="col-span-3"><Input placeholder="Measure label" value={m.label || ''} onChange={(e) => set(i, 'label', e.target.value)} /></div>
          <div className="col-span-2"><Input placeholder="Value (96%)" value={m.value || ''} onChange={(e) => set(i, 'value', e.target.value)} /></div>
          <div className="col-span-3"><Input placeholder="Labels: F, M, A, M" value={(m.labels || []).join(', ')} onChange={(e) => set(i, 'labels', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} /></div>
          <div className="col-span-3"><Input placeholder="Values: 91, 92, 95" value={((m.series && m.series[0] && m.series[0].values) || []).join(', ')} onChange={(e) => set(i, 'series', [{ values: e.target.value.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n)) }])} /></div>
          <button className="col-span-1 text-slate-400 hover:text-red-600 text-lg leading-none" onClick={() => onChange(charts.filter((_, j) => j !== i))}>×</button>
        </div>
      ))}
      {charts.length < max && <Btn onClick={() => onChange([...charts, {}])}>+ Mini chart</Btn>}
    </div>
  );
}

/** Monitoring metrics: name + monthly values + benchmark + direction + unit. */
function MetricsEditor({ value, onChange, max }) {
  const ms = value || [];
  const set = (i, k, v) => { const n = ms.map((x) => ({ ...x })); n[i] = { ...n[i], [k]: v }; onChange(n); };
  return (
    <div className="space-y-2">
      {ms.map((m, i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-2 space-y-1.5">
          <div className="grid grid-cols-12 gap-2 items-center">
            <div className="col-span-5"><Input placeholder="Metric name (e.g. UM timeliness)" value={m.name || ''} onChange={(e) => set(i, 'name', e.target.value)} /></div>
            <div className="col-span-6"><Input placeholder="Values: 94, 95, 97" value={(m.values || []).map((v) => (v == null ? '' : v)).join(', ')} onChange={(e) => set(i, 'values', e.target.value.split(',').map((x) => { const n = Number(x.trim()); return x.trim() === '' ? null : (Number.isFinite(n) ? n : null); }))} /></div>
            <button className="col-span-1 text-slate-400 hover:text-red-600 text-lg leading-none" onClick={() => onChange(ms.filter((_, j) => j !== i))}>×</button>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>Benchmark</span>
            <Input className="!w-20" placeholder="95" value={m.benchmark ?? ''} onChange={(e) => set(i, 'benchmark', e.target.value === '' ? null : Number(e.target.value))} />
            <select className="rounded-lg border border-slate-300 px-1.5 py-1.5 text-sm" value={m.dir || 'min'} onChange={(e) => set(i, 'dir', e.target.value)}>
              <option value="min">≥ compliant</option>
              <option value="max">≤ compliant</option>
            </select>
            <select className="rounded-lg border border-slate-300 px-1.5 py-1.5 text-sm" value={m.format || 'pct'} onChange={(e) => set(i, 'format', e.target.value)}>
              <option value="pct">%</option>
              <option value="number">number</option>
              <option value="time">seconds</option>
            </select>
          </div>
        </div>
      ))}
      {ms.length < max && <Btn onClick={() => onChange([...ms, { format: 'pct', dir: 'min' }])}>+ Metric</Btn>}
    </div>
  );
}

/** "Insert live data" — the differentiator. Server scopes the numbers. */
function InsertFromData({ feeds, kind, onInsert }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!feeds?.length) return null;
  return (
    <div className="relative inline-block">
      <Btn onClick={() => setOpen((o) => !o)} className="text-emerald-800 border-emerald-700/40">⤓ Insert live data</Btn>
      {open && (
        <div className="absolute z-30 mt-1 w-64 rounded-xl border border-slate-200 bg-white shadow-xl p-1">
          {feeds.map((f) => (
            <button key={f.key} disabled={busy}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 text-sm"
              onClick={async () => {
                setBusy(true);
                try {
                  const r = await jfetch(`/feeds/${f.key}`);
                  onInsert(kind, r.data);
                } catch (e) { alert(e.message); }
                setBusy(false); setOpen(false);
              }}>
              <div className="font-medium text-slate-800">{f.label}{f.scoped ? ' · your department' : ''}</div>
              <div className="text-xs text-slate-500">{f.desc}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldEditor({ field, slide, setSlide, feeds, enhance }) {
  const v = slide[field.key];
  const set = (val) => setSlide({ ...slide, [field.key]: val });
  const onInsert = (kind, data) => {
    if (kind === 'kpis' && data.kpis) set(data.kpis.slice(0, 4));
    if (kind === 'chart' && data.chart) set({ ...(slide[field.key] || {}), ...data.chart });
    if (kind === 'table' && data.table) set(data.table);
  };
  switch (field.type) {
    case 'text':
      return <Input maxLength={field.max} placeholder={field.label} value={v || ''} onChange={(e) => set(e.target.value)} />;
    case 'textarea':
      return (
        <div className="space-y-1">
          <TextArea rows={3} maxLength={field.max} placeholder={field.label} value={v || ''} onChange={(e) => set(e.target.value)} />
          {v && <Btn onClick={() => enhance(field.key, v)} className="text-emerald-800">✨ Refine with AI</Btn>}
        </div>
      );
    case 'bullets': return <BulletsEditor value={v} onChange={set} max={field.max} />;
    case 'csv':
      return <Input placeholder={field.label} value={(v || []).join(', ')} onChange={(e) => set(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />;
    case 'metrics': return <MetricsEditor value={v} onChange={set} max={field.max} />;
    case 'kpis': return <KpisEditor value={v} onChange={set} feeds={feeds} onInsert={onInsert} max={field.max || 4} />;
    case 'chart': return <ChartEditor value={v} onChange={set} feeds={feeds} onInsert={onInsert} />;
    case 'table': return <TableEditor value={v} onChange={set} feeds={feeds} onInsert={onInsert} />;
    case 'columns': return <ColumnsEditor value={v} onChange={set} max={field.max} />;
    case 'milestones': return <MilestonesEditor value={v} onChange={set} max={field.max} />;
    case 'multiples': return <MultiplesEditor value={v} onChange={set} max={field.max} />;
    default: return null;
  }
}

// ── Branding panel (compliance-level users only; server re-enforces) ────────

function BrandingPanel({ branding, onSaved, onClose }) {
  const [tokens, setTokens] = useState(branding.tokens);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setTokens({ ...tokens, [k]: v });
  const ColorRow = ({ k, label }) => (
    <div className="flex items-center gap-2">
      <input type="color" value={`#${tokens[k]}`} onChange={(e) => set(k, e.target.value.slice(1).toUpperCase())} className="h-8 w-10 rounded border border-slate-300" />
      <Input value={tokens[k]} onChange={(e) => set(k, e.target.value.replace(/^#/, ''))} className="w-24 font-mono text-xs" />
      <span className="text-sm text-slate-600">{label}</span>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">Organization brand</h2>
          <button className="text-slate-400 hover:text-slate-700 text-xl" onClick={onClose}>×</button>
        </div>
        <p className="text-sm text-slate-500 mb-4">These tokens style every deck anyone in the organization exports. Changes apply to future exports immediately.</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <ColorRow k="brand" label="Brand" />
          <ColorRow k="ink" label="Ink (headings, body)" />
          <ColorRow k="paper" label="Paper (background)" />
          <ColorRow k="surface" label="Surface (cards)" />
          <ColorRow k="muted" label="Muted text" />
          <ColorRow k="good" label="Positive" />
          <ColorRow k="warn" label="Warning" />
          <ColorRow k="bad" label="Negative" />
        </div>
        <div className="mt-4">
          <Label>Chart palette</Label>
          <div className="flex gap-2">
            {tokens.chart.map((c, i) => (
              <input key={i} type="color" value={`#${c}`} onChange={(e) => set('chart', tokens.chart.map((x, j) => (j === i ? e.target.value.slice(1).toUpperCase() : x)))} className="h-8 w-10 rounded border border-slate-300" />
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div><Label>Heading font</Label><Input value={tokens.font_head} onChange={(e) => set('font_head', e.target.value)} /></div>
          <div><Label>Body font</Label><Input value={tokens.font_body} onChange={(e) => set('font_body', e.target.value)} /></div>
        </div>
        <div className="mt-4"><Label>Footer text</Label><Input value={tokens.footer} onChange={(e) => set('footer', e.target.value)} placeholder="Shown at the bottom of every slide" /></div>
        <div className="mt-4 flex items-center gap-3">
          <div>
            <Label>Logo (PNG or JPEG, up to 1 MB)</Label>
            <input type="file" accept="image/png,image/jpeg" className="text-sm" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const fd = new FormData(); fd.append('logo', file);
              try { await jfetch('/branding/logo', { method: 'POST', body: fd }); onSaved(); } catch (err) { alert(err.message); }
            }} />
          </div>
          {branding.has_logo && (
            <div className="flex items-center gap-2">
              <img alt="logo" src={`${API}/api/deck-studio/branding/logo`} className="h-10 border border-slate-200 rounded p-1" />
              <Btn tone="danger" onClick={async () => {
                if (!window.confirm('Remove the organization logo from all future exports?')) return;
                try { await jfetch('/branding/logo', { method: 'DELETE' }); onSaved(); } catch (err) { alert(err.message); }
              }}>Remove logo</Btn>
            </div>
          )}
        </div>
        <div className="mt-4">
          <Label>PowerPoint template (.pptx or .potx)</Label>
          <input type="file" accept=".pptx,.potx" className="text-sm block" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const fd = new FormData(); fd.append('template', file);
            try {
              const r = await jfetch('/branding/template', { method: 'POST', body: fd });
              setTokens(r.tokens);
              onSaved();
            } catch (err) { alert(err.message); }
            e.target.value = '';
          }} />
          <p className="text-[11px] text-slate-400 mt-1">Upload the deck template your organization already uses: its colors and fonts are read from the file and become the brand above, applied to every future deck and themed export. Your logo and the status colors are not changed.</p>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">The logo and these colors are permanent for the whole organization: every deck anyone exports carries them until changed here.</p>
        <div className="mt-6 flex justify-end gap-2">
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn tone="primary" disabled={saving} onClick={async () => {
            setSaving(true);
            try { await jfetch('/branding', { method: 'PUT', body: JSON.stringify({ tokens }) }); onSaved(); onClose(); }
            catch (e) { alert(e.message); }
            setSaving(false);
          }}>{saving ? 'Saving…' : 'Save brand'}</Btn>
        </div>
      </div>
    </div>
  );
}

/**
 * Smart Add — paste anything, the server classifies it and offers ready
 * slides with real previews (rendered by the same engine that exports).
 */
function SmartAddModal({ onAdd, onClose }) {
  const [text, setText] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [previews, setPreviews] = useState({});
  const [busy, setBusy] = useState(false);

  const analyze = async (t) => {
    setBusy(true);
    try {
      const r = await jfetch('/suggest', { method: 'POST', body: JSON.stringify({ text: t }) });
      setSuggestions(r.suggestions);
      const pv = {};
      await Promise.all(r.suggestions.map(async (s, i) => {
        try {
          const p = await jfetch('/preview', { method: 'POST', body: JSON.stringify({ spec: { slides: [s.slide] } }) });
          pv[i] = { page: p.pages[0], theme: p.theme };
        } catch (e) { /* preview optional */ }
      }));
      setPreviews(pv);
    } catch (e) { alert(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-bold text-slate-900">Smart add</h2>
          <button className="text-slate-400 hover:text-slate-700 text-xl" onClick={onClose}>×</button>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          Paste anything — Excel cells, a list, labeled numbers, dated milestones, or a sentence. Deck Studio reads the shape of your data and proposes the slide that presents it best.
        </p>
        <TextArea rows={6} autoFocus placeholder={'Paste here…'} value={text}
          onChange={(e) => setText(e.target.value)}
          onPaste={(e) => {
            const t = e.clipboardData?.getData('text');
            if (t) { setText(t); analyze(t); e.preventDefault(); }
          }} />
        <div className="mt-2 flex justify-end">
          <Btn tone="primary" disabled={!text.trim() || busy} onClick={() => analyze(text)}>{busy ? 'Reading your data…' : 'Suggest slides'}</Btn>
        </div>
        {suggestions && !suggestions.length && (
          <div className="mt-4 text-sm text-slate-500">Nothing recognizable yet — try pasting a table, a list, or a sentence.</div>
        )}
        {suggestions && suggestions.length > 0 && (
          <div className="mt-4 space-y-3">
            {suggestions.map((s, i) => (
              <button key={i} className="w-full text-left rounded-xl border border-slate-200 hover:border-emerald-700 p-3 flex gap-4 items-center transition"
                onClick={() => { onAdd(s.slide); onClose(); }}>
                <div className="flex-shrink-0">
                  {previews[i] ? <SvgSlide page={previews[i].page} theme={previews[i].theme} width={220} /> : <div className="w-[220px] h-[124px] bg-slate-100 rounded" />}
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-800 capitalize">{s.slide.archetype}</div>
                  <div className="text-xs text-slate-500 mt-1">{s.reason}</div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Composer ────────────────────────────────────────────────────────────────

const FONT_OPTIONS = ['Segoe UI', 'Aptos', 'Calibri', 'Arial', 'Verdana', 'Tahoma', 'Trebuchet MS', 'Georgia', 'Garamond', 'Times New Roman'];

/** Per-deck theme: preset, main color, fonts. Layout and logo stay the org's. */
function ThemePopover({ theme, presets, onChange, onClose, canSetDefault, onDefaultSet }) {
  const t = theme || {};
  const set = (k, v) => {
    const n = { ...t, [k]: v };
    if (v === '' || v == null) delete n[k];
    onChange(Object.keys(n).length ? n : null);
  };
  return (
    <div className="absolute right-0 top-full mt-1 z-40 w-80 rounded-xl border border-slate-200 bg-white shadow-xl p-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-bold text-slate-900">Deck theme</div>
        <button className="text-slate-400 hover:text-slate-700" onClick={onClose}>×</button>
      </div>
      <Label>Theme</Label>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {presets.map((p) => (
          <button key={p.key}
            className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs ${((t.preset || 'org') === p.key) ? 'border-emerald-700 bg-emerald-900/5 font-semibold' : 'border-slate-300 hover:border-slate-400'}`}
            onClick={() => set('preset', p.key === 'org' ? '' : p.key)}>
            <span className="w-3 h-3 rounded-full border border-black/10" style={{ background: p.brand ? `#${p.brand}` : 'linear-gradient(135deg,#1f4a3f,#c97a2b)' }} />
            {p.label}
          </button>
        ))}
      </div>
      <Label>Main color</Label>
      <div className="flex items-center gap-2 mb-3">
        <input type="color" value={`#${t.brand || '1F4A3F'}`} onChange={(e) => set('brand', e.target.value.slice(1).toUpperCase())} className="h-8 w-10 rounded border border-slate-300" />
        <Input value={t.brand || ''} placeholder="org default" onChange={(e) => set('brand', e.target.value.replace(/^#/, '').toUpperCase())} className="w-28 font-mono text-xs" />
        {t.brand && <Btn onClick={() => set('brand', '')}>Reset</Btn>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Heading font</Label>
          <select className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" value={t.font_head || ''} onChange={(e) => set('font_head', e.target.value)}>
            <option value="">Org default</option>
            {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <Label>Body font</Label>
          <select className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" value={t.font_body || ''} onChange={(e) => set('font_body', e.target.value)}>
            <option value="">Org default</option>
            {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-3">Layout, logo, and footer always follow the organization. Theme changes restyle every slide and the export instantly.</p>
      {canSetDefault && Object.keys(t).length > 0 && (
        <div className="mt-3 border-t border-slate-200 pt-3">
          <Btn tone="primary" className="w-full" onClick={async () => {
            if (!window.confirm('Make this theme the permanent organization default? Every deck, for every user, will use it from now on.')) return;
            try {
              await jfetch('/branding/default-theme', { method: 'POST', body: JSON.stringify({ theme: t }) });
              onChange(null); // deck now follows the new org default
              onDefaultSet();
            } catch (e) { alert(e.message); }
          }}>★ Set as organization default</Btn>
          <p className="text-[11px] text-slate-400 mt-1.5">Writes this preset, color, and fonts into the permanent organization brand (audit-logged).</p>
        </div>
      )}
    </div>
  );
}

function Composer({ deck, archetypes, themePresets, feeds, branding, onBack, onDeckChange }) {
  const [spec, setSpec] = useState(deck.spec && Array.isArray(deck.spec.slides) ? deck.spec : { slides: [] });
  const [title, setTitle] = useState(deck.title);
  const [sel, setSel] = useState(0);
  const [pages, setPages] = useState([]);
  const [theme, setTheme] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [smartOpen, setSmartOpen] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [importDismissed, setImportDismissed] = useState(false);
  const debounceRef = useRef(null);

  const archMap = useMemo(() => Object.fromEntries(archetypes.map((a) => [a.key, a])), [archetypes]);

  const refreshPreview = useCallback((s) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await jfetch('/preview', { method: 'POST', body: JSON.stringify({ spec: s }) });
        setPages(r.pages); setTheme(r.theme); setWarnings(r.warnings || []);
      } catch (e) { /* keep last good preview */ }
    }, 350);
  }, []);

  useEffect(() => { refreshPreview(spec); }, [spec, refreshPreview]);

  const setSlide = (i, slide) => {
    const slides = spec.slides.map((s, j) => (j === i ? slide : s));
    setSpec((prev) => ({ ...prev, slides })); setDirty(true);
  };
  const addSlide = (key) => {
    const slides = [...spec.slides, { archetype: key }];
    setSpec((prev) => ({ ...prev, slides })); setSel(slides.length - 1); setAddOpen(false); setDirty(true);
  };
  const addPrebuiltSlide = (slide) => {
    const slides = [...spec.slides, slide];
    setSpec((prev) => ({ ...prev, slides })); setSel(slides.length - 1); setDirty(true);
  };
  const removeSlide = (i) => {
    const slides = spec.slides.filter((_, j) => j !== i);
    setSpec((prev) => ({ ...prev, slides })); setSel(Math.max(0, Math.min(sel, slides.length - 1))); setDirty(true);
  };
  const moveSlide = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= spec.slides.length) return;
    const slides = [...spec.slides];
    [slides[i], slides[j]] = [slides[j], slides[i]];
    setSpec((prev) => ({ ...prev, slides })); setSel(j); setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await jfetch(`/decks/${deck.id}`, { method: 'PUT', body: JSON.stringify({ title, spec }) });
      onDeckChange(r.deck); setDirty(false);
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const exportPptx = async () => {
    setExporting(true);
    try {
      if (dirty) await save();
      const r = await fetch(`${API}/api/deck-studio/export`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deckId: deck.id }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || 'Export failed');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${title || 'Deck'}.pptx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
    setExporting(false);
  };

  const enhance = async (i, key, text) => {
    try {
      const r = await jfetch('/enhance-text', { method: 'POST', body: JSON.stringify({ text, title: spec.slides[i]?.title || '' }) });
      setSlide(i, { ...spec.slides[i], [key]: r.enhanced });
    } catch (e) { alert(e.message); }
  };

  const cur = spec.slides[sel];
  const curArch = cur ? archMap[cur.archetype] : null;
  const curPages = pages.filter((p) => p.slideIndex === sel);
  const slideWarnings = warnings.filter((w) => w.startsWith(`Slide ${sel + 1}:`));

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200 bg-white">
        <button className="text-slate-500 hover:text-slate-800 text-sm" onClick={onBack}>← My decks</button>
        <input className="flex-1 text-lg font-bold text-slate-900 bg-transparent focus:outline-none focus:border-b focus:border-emerald-700" value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} />
        {dirty && <span className="text-xs text-amber-600 font-medium">unsaved changes</span>}
        <div className="relative">
          <Btn onClick={() => setThemeOpen((o) => !o)}>
            <span className="inline-block w-3 h-3 rounded-full mr-1.5 align-middle border border-black/10" style={{ background: `#${(spec.theme && spec.theme.brand) || (theme && theme.color.brand) || '1F4A3F'}` }} />
            Theme
          </Btn>
          {themeOpen && (
            <ThemePopover theme={spec.theme} presets={themePresets}
              canSetDefault={!!branding?.can_edit}
              onDefaultSet={() => { setThemeOpen(false); refreshPreview(spec); }}
              onChange={(t) => { const n = { ...spec }; if (t) n.theme = t; else delete n.theme; setSpec(n); setDirty(true); }}
              onClose={() => setThemeOpen(false)} />
          )}
        </div>
        <Btn onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Btn>
        <Btn tone="primary" onClick={exportPptx} disabled={exporting || !spec.slides.length}>{exporting ? 'Exporting…' : 'Export .pptx'}</Btn>
      </div>

      {/* An imported PowerPoint: what came across and what the house layout
          cannot carry, shown once so the author adds back what matters. */}
      {deck.import_report && !importDismissed && (
        <div className="px-5 py-2.5 bg-emerald-50 border-b border-emerald-200 text-xs text-emerald-900 flex items-start gap-3">
          <div className="flex-1">
            <span className="font-semibold">Imported from {deck.import_report.file_name}.</span>{' '}
            {deck.import_report.stats ? `${deck.import_report.stats.slides_in} slide${deck.import_report.stats.slides_in === 1 ? '' : 's'} in, ${deck.import_report.stats.slides_out} house slide${deck.import_report.stats.slides_out === 1 ? '' : 's'} out, in the organization's layout and brand. ` : ''}
            Every slide is editable; Export gives the native PowerPoint.
            {deck.import_report.warnings.length > 0 && (
              <ul className="mt-1 list-disc pl-4 space-y-0.5 text-emerald-800">
                {deck.import_report.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </div>
          <button className="text-emerald-700 hover:text-emerald-900 font-semibold" onClick={() => setImportDismissed(true)}>Dismiss</button>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* Slide rail */}
        <div className="w-56 border-r border-slate-200 bg-slate-50 overflow-y-auto p-3 space-y-2">
          {spec.slides.map((s, i) => {
            const firstPage = pages.find((p) => p.slideIndex === i);
            return (
              <div key={i} onClick={() => setSel(i)}
                className={`rounded-lg cursor-pointer border-2 p-1 ${i === sel ? 'border-emerald-700' : 'border-transparent hover:border-slate-300'}`}>
                <div className="text-[10px] text-slate-500 mb-0.5 flex items-center justify-between px-0.5">
                  <span>{i + 1} · {archMap[s.archetype]?.label || s.archetype}</span>
                  <span className="flex gap-1">
                    <button className="hover:text-slate-800" onClick={(e) => { e.stopPropagation(); moveSlide(i, -1); }}>↑</button>
                    <button className="hover:text-slate-800" onClick={(e) => { e.stopPropagation(); moveSlide(i, 1); }}>↓</button>
                    <button className="hover:text-red-600" onClick={(e) => { e.stopPropagation(); removeSlide(i); }}>×</button>
                  </span>
                </div>
                {firstPage && theme ? <SvgSlide page={firstPage} theme={theme} width={190} /> : <div className="h-[107px] bg-slate-200 rounded" />}
              </div>
            );
          })}
          <button className="w-full rounded-lg bg-emerald-900/5 border border-emerald-800/30 py-2.5 text-sm text-emerald-900 font-medium hover:bg-emerald-900/10" onClick={() => setSmartOpen(true)}>✨ Smart add (paste data)</button>
          <div className="relative">
            <button className="w-full rounded-lg border-2 border-dashed border-slate-300 py-3 text-sm text-slate-500 hover:border-emerald-700 hover:text-emerald-800" onClick={() => setAddOpen((o) => !o)}>+ Add slide</button>
            {addOpen && (
              <div className="absolute z-30 mt-1 w-64 rounded-xl border border-slate-200 bg-white shadow-xl p-1 max-h-80 overflow-auto">
                {archetypes.map((a) => (
                  <button key={a.key} className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100" onClick={() => addSlide(a.key)}>
                    <div className="text-sm font-medium text-slate-800">{a.label}</div>
                    <div className="text-xs text-slate-500">{a.desc}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Editor */}
        <div className="w-[26rem] border-r border-slate-200 overflow-y-auto p-4 bg-white">
          {cur && curArch ? (
            <div className="space-y-4">
              <div className="text-sm font-bold text-slate-900">{curArch.label}</div>
              <div className="text-xs text-slate-500 -mt-3">{curArch.desc}</div>
              {curArch.fields.map((f) => (
                // Keyed by SLIDE + field: editors keep uncontrolled state
                // (TableEditor's textarea), and two slides of the same
                // archetype must never share a mounted editor — the stale
                // text would overwrite the second slide's data on blur.
                <div key={`${sel}:${f.key}`}>
                  <Label>{f.label}</Label>
                  <FieldEditor field={f} slide={cur} setSlide={(s) => setSlide(sel, s)} feeds={feeds}
                    enhance={(key, text) => enhance(sel, key, text)} />
                </div>
              ))}
              {slideWarnings.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800 space-y-0.5">
                  {slideWarnings.map((w, i) => <div key={i}>{w.replace(`Slide ${sel + 1}: `, '')}</div>)}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-slate-500 mt-8 text-center">Add a slide to start.</div>
          )}
        </div>

        {/* Preview */}
        <div className="flex-1 overflow-y-auto bg-slate-100 p-6">
          <div className="text-[11px] text-slate-500 mb-3">
            Live preview is computed by the same engine that writes the PowerPoint file. Friendly reminder: decks travel — keep PHI out of slide content.
          </div>
          <div className="space-y-4 flex flex-col items-center">
            {curPages.length ? curPages.map((p, i) => (
              <SvgSlide key={i} page={p} theme={theme} width={760} pageNumber={pages.indexOf(p) + 1} />
            )) : cur ? <div className="text-sm text-slate-400 mt-10">Rendering…</div> : null}
          </div>
        </div>
      </div>
      {smartOpen && <SmartAddModal onAdd={addPrebuiltSlide} onClose={() => setSmartOpen(false)} />}
    </div>
  );
}

/**
 * Board / Committee deck builder — compliance-level users only (server
 * re-enforces). Pick the audience, pick one or more reporting quarters, and
 * the platform composes the governance deck from live module data. The result
 * is a saved, editable deck like any other.
 */
function BoardDeckModal({ onCreated, onClose }) {
  const [audience, setAudience] = useState('committee');
  const [quarters, setQuarters] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    jfetch('/board-quarters')
      .then((r) => {
        setQuarters(r.quarters);
        const prior = r.quarters.find((q) => !q.current) || r.quarters[0];
        if (prior) setSelected(new Set([prior.key]));
      })
      .catch((e) => setError(e.message));
  }, []);

  const toggle = (key) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else if (n.size < 4) n.add(key);
      return n;
    });
  };

  const generate = async () => {
    setBusy(true); setError(null);
    try {
      const periods = (quarters || []).filter((q) => selected.has(q.key)).map((q) => ({ year: q.year, quarter: q.quarter }));
      const r = await jfetch('/generate-board', { method: 'POST', body: JSON.stringify({ audience, periods }) });
      onCreated(r.deck);
    } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-bold text-slate-900">Board & Committee deck</h2>
          <button className="text-slate-400 hover:text-slate-700 text-xl" onClick={onClose}>×</button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Built from live module data for the quarters you pick: risk, issues, privacy, audits, the work plan and
          its monitoring board, CAPs, vendor oversight with grades, FWA casework, policies, memos, and the quarterly
          operational metrics. Everything open and everything closed in the window. Every slide stays editable.
        </p>

        <Label>Audience</Label>
        <div className="flex rounded-xl bg-slate-100 p-1 mb-4">
          {[['committee', 'Compliance Committee'], ['board', 'Board of Directors']].map(([key, label]) => (
            <button key={key}
              className={`flex-1 rounded-lg py-2 text-sm font-medium transition ${audience === key ? 'bg-white shadow text-emerald-900' : 'text-slate-500 hover:text-slate-800'}`}
              onClick={() => setAudience(key)}>
              {label}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500 -mt-2 mb-4">
          {audience === 'board'
            ? 'Executive edition: the glance wall, escalations, risk temperature, five-quarter trends, and the top risks.'
            : 'Full edition: a section per module with KPIs, quarterly trends, monitored measures, and detail tables.'}
        </p>

        <Label>Reporting quarters (up to 4)</Label>
        {!quarters && !error && <div className="text-sm text-slate-500 py-3">Loading quarters…</div>}
        {quarters && (
          <div className="grid grid-cols-4 gap-2 mb-1">
            {quarters.map((q) => (
              <button key={q.key} onClick={() => toggle(q.key)}
                className={`relative rounded-lg border px-2 py-2 text-sm font-medium transition
                  ${selected.has(q.key) ? 'border-emerald-700 bg-emerald-900/5 text-emerald-900' : 'border-slate-300 text-slate-600 hover:border-slate-400'}`}>
                {q.label}
                {q.current && <span className="block text-[9px] font-normal text-slate-400">current</span>}
                {q.has_operational_metrics && (
                  <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-emerald-600"
                    title="Operational metrics entered for this quarter" />
                )}
              </button>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-400 mb-4">
          A green dot marks quarters with operational metrics entered. Selecting several quarters builds one deck
          spanning the whole window.
        </p>

        {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-2 mb-3">{error}</div>}
        <div className="flex justify-end gap-2">
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn tone="primary" disabled={busy || !selected.size} onClick={generate}>
            {busy ? 'Building from live data…' : selected.size ? `Build deck · ${selected.size} quarter${selected.size === 1 ? '' : 's'}` : 'Select a quarter'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── App shell: deck list + composer ─────────────────────────────────────────

export default function DeckStudioApp({ user }) {
  const [decks, setDecks] = useState(null);
  const [archetypes, setArchetypes] = useState([]);
  const [themePresets, setThemePresets] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [branding, setBranding] = useState(null);
  const [open, setOpen] = useState(null);
  const [brandOpen, setBrandOpen] = useState(false);
  const [boardOpen, setBoardOpen] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState('');

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await jfetch('/generate', { method: 'POST', body: JSON.stringify({}) });
      setOpen(r.deck);
    } catch (e) { alert(e.message); }
    setGenerating(false);
  };

  const reload = useCallback(() => {
    Promise.all([jfetch(`/decks${showArchived ? '?archived=1' : ''}`), jfetch('/archetypes'), jfetch('/feeds'), jfetch('/branding')])
      .then(([d, a, f, b]) => { setDecks(d.decks); setArchetypes(a.archetypes); setThemePresets(a.theme_presets || []); setFeeds(f.feeds); setBranding(b); })
      .catch((e) => setError(e.message));
  }, [showArchived]);
  useEffect(() => { reload(); }, [reload]);

  if (error) return <div className="p-8 text-sm text-red-700">Deck Studio failed to load: {error}</div>;
  if (!decks || !branding) return <div className="p-8 text-sm text-slate-500">Loading Deck Studio…</div>;

  if (open) {
    return (
      <Composer deck={open} archetypes={archetypes} themePresets={themePresets} feeds={feeds} branding={branding}
        onBack={() => { setOpen(null); reload(); }}
        onDeckChange={(d) => setOpen((o) => ({ ...o, ...d }))} />
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-slate-900">Deck Studio</h1>
        <div className="flex gap-2">
          {branding.can_edit && <Btn onClick={() => setBrandOpen(true)}>Organization brand</Btn>}
          <Btn onClick={async () => {
            try { const r = await jfetch('/decks', { method: 'POST', body: JSON.stringify({ title: 'Untitled deck' }) }); setOpen(r.deck); }
            catch (e) { alert(e.message); }
          }}>+ Blank deck</Btn>
          <label className={`inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 ${importing ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
            title="Upload a PowerPoint made elsewhere; its slides are rebuilt in the house layout and brand, then every slide stays editable">
            {importing ? 'Importing…' : '⤒ Import PowerPoint'}
            <input type="file" accept=".pptx" className="hidden" disabled={importing} onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              setImporting(true);
              try {
                const fd = new FormData(); fd.append('deck', file);
                const r = await jfetch('/decks/import', { method: 'POST', body: fd });
                setOpen({ ...r.deck, import_report: { warnings: r.warnings || [], stats: r.stats || null, file_name: file.name } });
              } catch (err) { alert(err.message); }
              setImporting(false);
            }} />
          </label>
          <Btn onClick={generate} disabled={generating}>
            {generating ? 'Building…' : '⚡ Generate from live data'}
          </Btn>
          {branding.can_edit && (
            <Btn tone="primary" onClick={() => setBoardOpen(true)}>Board & Committee deck</Btn>
          )}
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        One click builds a complete, org-branded deck from the live data you are allowed to see — risks, issues, work plan, CAPs{branding.can_edit ? ', privacy, memos, and operational metrics' : ''} — then every slide stays editable. Exports are native, editable PowerPoint files.
      </p>
      {decks.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 p-16 text-center text-slate-500">
          <div className="text-lg font-medium text-slate-700 mb-1">No decks yet</div>
          <div className="text-sm mb-4">Generate a full deck from your live data, or start blank and compose your own.</div>
          <Btn tone="primary" onClick={generate} disabled={generating}>
            {generating ? 'Building from your data…' : '⚡ Generate from live data'}
          </Btn>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {decks.map((d) => (
            <div key={d.id} className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-md transition cursor-pointer"
              onClick={async () => {
                if (renaming === d.id) return;
                try { const r = await jfetch(`/decks/${d.id}`); setOpen(r.deck); } catch (e) { alert(e.message); }
              }}>
              {renaming === d.id ? (
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <Input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Escape') setRenaming(null);
                      if (e.key === 'Enter' && renameVal.trim()) {
                        try { await jfetch(`/decks/${d.id}`, { method: 'PUT', body: JSON.stringify({ title: renameVal.trim() }) }); setRenaming(null); reload(); }
                        catch (err) { alert(err.message); }
                      }
                    }} />
                </div>
              ) : (
                <div className="font-semibold text-slate-900 truncate">{d.title}</div>
              )}
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                <span>{d.slide_count} slide{d.slide_count === 1 ? '' : 's'} · updated {new Date(d.updated_at).toLocaleDateString()}</span>
                {d.audience === 'board' && <span className="rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-0.5 text-[10px] font-semibold">Board</span>}
                {d.audience === 'committee' && <span className="rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[10px] font-semibold">Committee</span>}
              </div>
              <div className="mt-3 flex justify-end gap-1 text-xs" onClick={(e) => e.stopPropagation()}>
                <Btn onClick={() => { setRenaming(d.id); setRenameVal(d.title); }}>Rename</Btn>
                <Btn onClick={async () => {
                  try { await jfetch(`/decks/${d.id}/archive`, { method: 'POST', body: JSON.stringify({ restore: showArchived }) }); reload(); }
                  catch (err) { alert(err.message); }
                }}>{showArchived ? 'Restore' : 'Archive'}</Btn>
                <Btn tone="danger" onClick={async () => {
                  if (!window.confirm(`Delete "${d.title}"? This cannot be undone.`)) return;
                  try { await jfetch(`/decks/${d.id}`, { method: 'DELETE' }); reload(); } catch (err) { alert(err.message); }
                }}>Delete</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-6">
        <button className="text-sm text-slate-500 hover:text-slate-800 underline underline-offset-2"
          onClick={() => setShowArchived((v) => !v)}>
          {showArchived ? '← Back to active decks' : 'View archived decks'}
        </button>
      </div>
      {brandOpen && <BrandingPanel branding={branding} onSaved={reload} onClose={() => setBrandOpen(false)} />}
      {boardOpen && <BoardDeckModal onCreated={(d) => { setBoardOpen(false); setOpen(d); }} onClose={() => setBoardOpen(false)} />}
    </div>
  );
}
